import { LocalDate } from '@js-joda/core'
import {
    AccountType,
    AggregatedAnalyticsByProperty,
    ClientAccount,
    CumulativeBundleAnalytics,
    EmissionFactorLogicalUnit,
    SingleShippingEmissionEstimate,
} from '@lune-climate/lune'
import { SuccessResponseWithMeta } from '@lune-climate/lune/esm/core/SuccessResponse'
import {
    AnalyticsMetrics,
    BatchUpdateLogisticsAnnotationsRequest,
    BatchUpdateLogisticsAnnotationsResponse,
    ClientAccountInformation,
    CreateApiKeyRequest,
    CreatePaymentMethodResponse,
    DataQualityAggregatedTimeseries,
    DataQualityOverallScores,
    DataSheet,
    DataSheetAnnotationsUpdateRequest,
    EmissionFactor,
    Errors as DapiErrorsResponse,
    EstimateSource,
    GetBusinessEstimateAnalyticsResponse,
    ImprovementSuggestion,
    InferredRfqColumnAndValueMappings,
    InferredRfqValueMappings,
    Organisation,
    OrganisationUsers,
    PaginatedBusinessEstimatesWithAccountInfo,
    PaginatedDataSheetBusinessEstimates,
    PaginatedDataSheetLogisticsEstimates,
    PaginatedDataSheets,
    PaginatedEmissionFactors,
    PaginatedEstimatesWithAccountInfo,
    PaginatedLogisticsEstimatesWithAccountInfo,
    PaginatedOrdersWithAccountInfo,
    PaginatedProposals,
    PartialDataSheet,
    PatchOrganisationRequest,
    PaymentMethodInitiation,
    PaymentMethods,
    ProposalRequest,
    ProposalResponse,
    PublicProposalResponse,
    RfqColumnAndValueInferenceRequest,
    RfqTemplate,
    RfqValueInferenceRequest,
    SeaEmissionsAggregatedByCountryLaneAndCarrier,
    SeaEmissionsAggregatedByRouteAndCarrier,
    SeaShippingMetrics,
    SeaShippingRecommendations,
    ShippingEmissionTimeseries,
    ThirtyDayStatistics,
    WtwBreakdownTimeseries,
} from '@lune-fe/dapi-models'
import { wrapAxiosPromise } from '@lune-fe/lune-components-lib'
import { AxiosError } from 'axios'
import { AsyncResult } from 'ts-results-es'

import { AccountMini } from 'components/AccountFilter'
import {
    bootstrapAxiosClient,
    bootstrapAxiosClientNoInterceptors,
    encodeDateTime,
} from 'endpoints/util'
import { IAccount } from 'models/account'
import { IActivity } from 'models/activity'
import { ApiKeyStatus, IApiKey, IApiKeyWithSecret } from 'models/apiKey'
import { OrderStatus } from 'models/order'
import { IPaginationQueryParams, IPaginationResult } from 'models/pagination'
import { IUser } from 'models/user'
import { EstimateType } from 'views/CalculateEmissions'
import { RfqTemplateData } from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import { TypedDapiRfqTemplate } from 'views/CalculateEmissions/RFQ/utils/types'
import { PaginatedInvoices } from 'views/Settings/Billing/Billing'

export type DapiErrors = DapiErrorsResponse
export type DapiOrder = PaginatedOrdersWithAccountInfo['data'][number]

let dapi = bootstrapAxiosClient(`${process.env.REACT_APP_DAPI_URL}`)
let dapiNoInterceptors = bootstrapAxiosClientNoInterceptors(`${process.env.REACT_APP_DAPI_URL}`)

export const bootstrapDapi = () => {
    dapi = bootstrapAxiosClient(`${process.env.REACT_APP_DAPI_URL}`)
    dapiNoInterceptors = bootstrapAxiosClientNoInterceptors(`${process.env.REACT_APP_DAPI_URL}`)
}

export const resetPassword = async (data: { password: string; token: string }): Promise<any> =>
    (await dapi.post(`/users/reset-password`, data)).data

export const verifyEmail = async (token: string) =>
    (await dapi.get(`/users/email-verification/${token}`)).data

export const getUserData = async () => (await dapi.get(`/users`)).data

interface IFormData {
    email: string
    password: string
}

type SignupArg = {
    email?: string
    firstname: string
    lastname: string
    marketingConsent?: boolean
    password?: string
    accessToken?: string
    currency?: string
    organisationName?: string
    invitationId?: string
}

export const signup = async (
    signupPayload: SignupArg,
): Promise<{
    user: IUser
    account: IAccount
    token?: string
    docsPublishableKey: string
}> => (await dapi.post(`/v1/users`, signupPayload)).data

export const getAccounts = async ({
    after,
    limit,
}: {
    after?: string
    limit?: number
}): Promise<IPaginationResult<IAccount>> => {
    return (
        await dapi.get(`v1/users/accounts`, {
            params: {
                ...(after && { after }),
                ...(limit && { limit }),
            },
        })
    ).data
}

export const getTeamData = async (): Promise<IUser[]> => (await dapi.get(`/accounts/team`)).data

export const getApiKeys = async (): Promise<IApiKey[]> => (await dapi.get(`/api-keys`)).data

export const rotateApiKeys = async (apiKeyId: string): Promise<IApiKeyWithSecret> => {
    return (await dapi.put(`/api-keys/${apiKeyId}/rotate`)).data
}

export const updateApiKeyStatus = async (apiKeyId: string, status: ApiKeyStatus): Promise<void> =>
    (await dapi.patch(`/api-keys/${apiKeyId}`, { status })).data

export const createApiKey = async (data: CreateApiKeyRequest): Promise<string> =>
    (await dapi.post(`/v1/api-keys`, data)).data

export const deleteApiKey = async (apiKeyId: string): Promise<void> =>
    (await dapi.delete(`/api-keys/${apiKeyId}`)).data

export const googleAuthorisation = async (data: {
    redirectUrl: string
}): Promise<{ authorisationUrl: string }> =>
    (await dapi.post(`/v1/users/google-authorisation`, data)).data

export type GoogleSSOUserProfile = {
    accessToken: string
    email: string
    firstname?: string
    lastname?: string
    avatar?: string
}

export const googleVerifyAuthorisationCode = async (data: {
    code: string
    redirectUrl: string
}): Promise<GoogleSSOUserProfile> =>
    (await dapi.post(`/v1/users/google-verify-authorisation-code`, data)).data

type LoginArg = {
    email?: string
    password?: string
    accessToken?: string
}

export const login = async (
    data: LoginArg,
): Promise<{
    user: IUser
    account: IAccount
    isAdmin: boolean
    token?: string
    docsPublishableKey: string
}> => (await dapi.post(`/v1/users/login`, data)).data

export const logout = async (): Promise<void> => (await dapi.post(`/users/logout`)).data

export const getActivity = async (after?: string): Promise<IPaginationResult<IActivity>> =>
    (await dapi.get(`/activity${after ? `?after=${after}` : ''}`)).data

export const requestPasswordReset = async (email: string): Promise<IFormData> =>
    (await dapi.post(`/users/forgot-password`, { email })).data

export const getUserAccounts = async (): Promise<IAccount[]> =>
    (await dapi.get(`users/accounts`)).data

export const setUserPrimaryAccountId = async (primaryAccountId: number | string): Promise<void> =>
    (await dapi.post(`/users/primary-account`, { id: primaryAccountId })).data

export const getUserSettings = async (): Promise<any> => (await dapi.get(`/users/settings`)).data

export const setUserSettings = async (settings: object): Promise<any> =>
    (await dapi.put('/users/settings', settings)).data

export const getPaymentMethods = async (): Promise<PaymentMethods> =>
    (await dapi.get(`/v1/payment-methods`)).data

export const deletePaymentMethod = async (stripeId: string): Promise<void> =>
    (await dapi.delete(`/v1/payment-methods/${stripeId}`)).data

export const createPaymentMethodInitiation = async (): Promise<PaymentMethodInitiation> =>
    (await dapi.post(`/v1/payment-methods`)).data

export const uploadLogo = (
    fd: FormData,
): Promise<{
    data: {
        url: string
    }
}> =>
    dapiNoInterceptors.post(`/accounts/logo`, fd, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })

export const getUserOrganisations = async (): Promise<Organisation[]> =>
    (await dapi.get(`/v1/organisations`)).data
export const getOrganisation = async (id: string): Promise<Organisation> =>
    (await dapi.get(`/v1/organisations/${id}`)).data
export const getOrganisationUsers = async (id: string): Promise<OrganisationUsers> =>
    (await dapi.get(`/v1/organisations/${id}/users`)).data
export const patchOrganisation = async (
    id: string,
    organisation: PatchOrganisationRequest,
): Promise<Organisation> => (await dapi.patch(`/v1/organisations/${id}`, organisation)).data
export const uploadOrganisationLogo = (
    id: string,
    fd: FormData,
): Promise<{
    data: {
        url: string
    }
}> =>
    dapiNoInterceptors.post(`/v1/organisations/${id}/logo`, fd, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })

export const promoteOrganisationAdmin = async (
    organisationId: string,
    userId: string,
): Promise<any> => (await dapi.put(`v1/organisations/${organisationId}/users/${userId}/admin`)).data
export const demoteOrganisationAdmin = async (
    organisationId: string,
    userId: string,
): Promise<any> =>
    (await dapi.delete(`v1/organisations/${organisationId}/users/${userId}/admin`)).data
export const removeFromOrganisation = async (
    organisationId: string,
    userId: string,
): Promise<any> => (await dapi.delete(`v1/organisations/${organisationId}/users/${userId}`)).data
export const inviteToOrganisation = async (organisationId: string, email: string): Promise<any> =>
    (await dapi.post(`v1/organisations/${organisationId}/invite`, { email })).data
export const removeInviteFromOrganisation = async (
    organisationId: string,
    inviteId: string,
): Promise<any> => (await dapi.delete(`v1/organisations/${organisationId}/invite/${inviteId}`)).data

export const getOrders = async ({
    after,
    limit,
    accountId,
    status,
    from,
    through,
}: IPaginationQueryParams & {
    accountId?: string[]
    status?: OrderStatus[]
    from?: Date
    through?: Date
} = {}): Promise<PaginatedOrdersWithAccountInfo> => {
    // Need to explicitly encode from and through, axios doesn't work
    const queryString = []
    if (from) {
        queryString.push(`from=${encodeURIComponent(from.toISOString())}`)
    }
    if (through) {
        queryString.push(`through=${encodeURIComponent(through.toISOString())}`)
    }
    return (
        await dapi.get(`v1/orders?${queryString.join('&')}`, {
            params: {
                ...(after && { after }),
                ...(limit && { limit }),
                ...(accountId && { accountId }),
                ...(status && { status }),
            },
        })
    ).data
}

export const getAccountsById = async ({
    accountId,
}: {
    accountId: string[]
}): Promise<AccountMini[]> => {
    return (
        await dapi.get(`v1/accounts`, {
            params: {
                ...(accountId.length && { accountId }),
            },
        })
    ).data
}

export const getRecentlyUsedAccounts = async ({
    name,
    type,
}: {
    name?: string
    type?: AccountType
}): Promise<AccountMini[]> => {
    const sanitisedName = name ? name.trim() : undefined

    return (
        await dapi.get(`v1/accounts/recently-used`, {
            params: {
                ...(sanitisedName && { name: sanitisedName }),
                ...(type && { type }),
            },
        })
    ).data
}

export const getShippingEstimate = async (
    id: string,
    token: string,
): Promise<SuccessResponseWithMeta<SingleShippingEmissionEstimate>> => {
    const customApi = bootstrapAxiosClient(`${process.env.REACT_APP_API_URL}`, token)
    return (await customApi.get(`v1/estimates/shipping/${id}`)).data
}

export const getEstimates = async ({
    after,
    limit,
    accountId,
    from,
    through,
    type,
}: IPaginationQueryParams & {
    accountId?: string[]
    type?: EstimateType[]
    from?: Date
    through?: Date
} = {}): Promise<PaginatedEstimatesWithAccountInfo> => {
    // Need to explicitly encode from and through, axios doesn't work
    const queryString = []
    if (from) {
        queryString.push(`from=${encodeURIComponent(from.toISOString())}`)
    }
    if (through) {
        queryString.push(`through=${encodeURIComponent(through.toISOString())}`)
    }
    return (
        await dapi.get(`v1/estimates?${queryString.join('&')}`, {
            params: {
                ...(after && { after }),
                ...(limit && { limit }),
                ...(accountId && { accountId }),
                ...(type && { type }),
            },
        })
    ).data
}

export const getInvoices = async ({
    after,
    limit,
}: IPaginationQueryParams): Promise<PaginatedInvoices> => {
    return await dapi.get(`/v1/invoices`, {
        params: {
            ...(after && { after }),
            ...(limit && { limit }),
        },
    })
}

export const setupPaymentIntent = async (): Promise<CreatePaymentMethodResponse> =>
    (await dapi.post(`v1/payment-methods/setup-intent`)).data

export const listEmissionFactors = async ({
    limit,
    after,
    name,
    source,
    publicationYear,
    region,
    unit,
    category,
}: IPaginationQueryParams & {
    name?: string
    source?: EmissionFactor['source'][]
    publicationYear?: number[]
    region?: string[]
    unit?: EmissionFactorLogicalUnit
    category?: string[]
}): Promise<PaginatedEmissionFactors> => {
    return (
        await dapi.get('/v1/emission-factors', {
            params: {
                limit,
                after,
                name,
                source,
                publicationYear,
                region,
                unit,
                category,
            },
        })
    ).data
}

export const uploadDataSheet = (
    fd: FormData,
    type: 'fintech' | 'logistics' | 'rfq' | 'logistics-user-template',
): Promise<{
    data: {
        url: string
    }
}> =>
    dapiNoInterceptors.post(`/v1/data-sheets/${type}`, fd, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })

export const getDataSheets = async (
    {
        after,
        limit,
    }: {
        after?: string
        limit?: number
    },
    type: 'fintech' | 'logistics' | 'rfq',
): Promise<IPaginationResult<DataSheet>> => {
    return (
        await dapi.get(`v1/data-sheets/${type}`, {
            params: {
                ...(after && { after }),
                ...(limit && { limit }),
            },
        })
    ).data
}

export const downloadDataSheet = async (
    id: string,
    accountId?: string | null,
): Promise<ArrayBuffer> => {
    // To download a datasheet one must be the owner, but templates show for
    // the whole organisation. Editing a template uses the original data sheet
    // so let us perform the operation against the account that created it
    const headers = !accountId ? {} : { headers: { 'Lune-Account': accountId } }
    return (
        await dapiNoInterceptors.get(`v1/data-sheets/${id}/download`, {
            responseType: 'arraybuffer',
            ...headers,
        })
    ).data
}

export const createProposal = async (data: ProposalRequest): Promise<ProposalResponse> =>
    (await dapi.post(`/v1/proposals`, data)).data

export const getProposalById = async (id: string): Promise<ProposalResponse> =>
    (await dapi.get(`v1/proposals/${id}`)).data

export const getPublicProposalById = async (id: string): Promise<PublicProposalResponse> =>
    (await dapi.get(`v1/proposals/public/${id}`)).data

export const getProposals = async ({
    after,
    limit,
}: {
    after?: string
    limit?: number
}): Promise<PaginatedProposals> => {
    return (
        await dapi.get(`v1/proposals`, {
            params: {
                ...(after && { after }),
                ...(limit && { limit }),
            },
        })
    ).data
}

export const getCumulativeAnalyticsPerBundle = async ({
    from,
    through,
    accountIds,
}: {
    from?: LocalDate
    through?: LocalDate
    accountIds?: string[]
}): Promise<CumulativeBundleAnalytics> => {
    return (
        await dapi.get(`v1/analytics/cumulative-per-bundle`, {
            params: {
                ...(from && { from: from.toString() }),
                ...(through && { through: through.toString() }),
                ...(accountIds && { accountId: accountIds }),
            },
        })
    ).data
}

export const getShippingEmissionTimeseries = async ({
    from,
    through,
    interval,
    accountIds,
}: {
    from?: LocalDate
    through?: LocalDate
    interval?: 'day' | 'seven_days' | 'month'
    accountIds?: string[]
}): Promise<{
    wtwBreakdownTimeseries: WtwBreakdownTimeseries
    emissionTimeseries: ShippingEmissionTimeseries
    carbonIntensityTimeseries: {
        date: string
        average: number
    }[]
    carbonIntensityTotal: number
    // Commented out to make sure we don't use it.
    //
    // Remove from DAPI once confirmed unused.
    //
    // account: {
    //     name: string
    //     type: 'live' | 'test'
    //     logo: string | null
    // }
}> => {
    return (
        await dapi.get(`v1/analytics/shipping/estimates`, {
            params: {
                ...(from && { from: from.toString() }),
                ...(through && { through: through.toString() }),
                ...(interval && { interval }),
                ...(accountIds && { accountIds }),
            },
        })
    ).data
}

export const getAggregatedAnalyticsByProperty = async ({
    from,
    through,
    accountIds,
}: {
    from?: LocalDate
    through?: LocalDate
    accountIds?: string[]
}): Promise<AggregatedAnalyticsByProperty> => {
    return (
        await dapi.get(`v1/analytics/aggregated-by-property`, {
            params: {
                ...(from && { from: from.toString() }),
                ...(through && { through: through.toString() }),
                ...(accountIds && { accountId: accountIds }),
            },
        })
    ).data
}

export const getMetrics = async ({
    from,
    through,
    accountIds,
}: {
    from?: LocalDate
    through?: LocalDate
    accountIds?: string[]
}): Promise<AnalyticsMetrics> => {
    return (
        await dapi.get(`v1/analytics/metrics`, {
            params: {
                ...(from && { from: from.toString() }),
                ...(through && { through: through.toString() }),
                ...(accountIds && { accountId: accountIds }),
            },
        })
    ).data
}

export const getOverallDataHealthScores = async (
    from?: LocalDate,
    through?: LocalDate,
    accountIds?: string[],
): Promise<DataQualityOverallScores> => {
    return (
        await dapi.get(`v1/data-quality/overall-scores`, {
            params: {
                ...(from && { from: from.toString() }),
                ...(through && { through: through.toString() }),
                account_ids: accountIds,
            },
        })
    ).data
}

export const getDataHeathImprovements = async (): Promise<{
    mode: ImprovementSuggestion[] | null
    load: ImprovementSuggestion[] | null
    route: ImprovementSuggestion[] | null
}> => {
    return (await dapi.get(`v1/data-quality/improvements`)).data
}

export const getDataQualityAggregatedTimeseries = async ({
    from,
    through,
    interval,
    accountIds,
    isShipment,
}: {
    from?: LocalDate
    through?: LocalDate
    interval?: 'day' | 'month'
    accountIds?: string[]
    isShipment?: boolean
}): Promise<DataQualityAggregatedTimeseries> => {
    return (
        await dapi.get(`v1/data-quality/aggregated-timeseries`, {
            params: {
                ...(from && { from: from.toString() }),
                ...(through && { through: through.toString() }),
                ...(interval && { interval }),
                ...(accountIds && { accountId: accountIds }),
                ...(isShipment !== undefined && { is_shipment: isShipment }),
            },
        })
    ).data
}

export const getEmissionFactorVersions = async (
    id: string,
): Promise<{ versions: EmissionFactor[]; identifiedIndex: number }> => {
    return (await dapi.get(`v1/emission-factors/${id}/versions`)).data
}

export const requestAccessToReduceEmissions = async (): Promise<void> => {
    return (await dapi.post(`v1/reduce-emissions/request-access`)).data
}

export const getSeaShippingMetrics = async (
    organisationId?: string,
): Promise<SeaShippingMetrics> => {
    return (
        await dapi.get(`v1/reduce-emissions/sea/headline-metrics`, {
            params: {
                ...(organisationId && { organisation_id: organisationId }),
            },
        })
    ).data
}

export const getRouteAndCarrierMetrics = async (
    organisationId?: string,
): Promise<SeaEmissionsAggregatedByRouteAndCarrier> => {
    return (
        await dapi.get(`v1/reduce-emissions/sea/route-and-carrier`, {
            params: {
                ...(organisationId && { organisation_id: organisationId }),
            },
        })
    ).data
}

export const getCountryLaneAndCarrierMetrics = async (
    organisationId?: string,
): Promise<SeaEmissionsAggregatedByCountryLaneAndCarrier> => {
    return (
        await dapi.get(`v1/reduce-emissions/sea/country-lane-and-carrier`, {
            params: {
                ...(organisationId && { organisation_id: organisationId }),
            },
        })
    ).data
}

export const getSeaShippingRecommendations = async (
    organisationId?: string,
): Promise<SeaShippingRecommendations> => {
    return (
        await dapi.get(`v1/reduce-emissions/sea/recommendations`, {
            params: { ...(organisationId && { organisation_id: organisationId }) },
        })
    ).data
}

export const createRfqTemplate = async (
    name: string,
    template: RfqTemplateData,
    sheetType: RfqTemplate['sheetType'],
): Promise<TypedDapiRfqTemplate> => {
    return (
        await dapiNoInterceptors.post(`/v1/rfq-templates`, {
            name,
            template,
            sheet_type: sheetType,
        })
    ).data
}

export const getRfqTemplates = async ({
    sheetType,
    after,
    limit,
}: {
    sheetType: RfqTemplate['sheetType']
    after?: string
    limit?: number
}): Promise<IPaginationResult<TypedDapiRfqTemplate>> => {
    const response: {
        has_more: boolean
        next_cursor: string | null
        data: TypedDapiRfqTemplate[]
    } = (
        await dapiNoInterceptors.get(`v1/rfq-templates`, {
            params: {
                type: sheetType,
                ...(after && { after }),
                ...(limit && { limit }),
            },
        })
    ).data

    return {
        hasMore: response.has_more,
        nextCursor: response.next_cursor,
        data: response.data,
    }
}

export function inferRfqValueMappings(
    data: RfqValueInferenceRequest,
): AsyncResult<InferredRfqValueMappings, AxiosError> {
    return wrapAxiosPromise(
        dapi.post(`/v1/rfq-inference/values`, data).then((response) => response.data),
    )
}

export function inferRfqMappings(
    data: RfqColumnAndValueInferenceRequest,
): AsyncResult<InferredRfqColumnAndValueMappings, AxiosError> {
    return wrapAxiosPromise(dapi.post(`/v1/rfq-inference`, data).then((response) => response.data))
}

export const deleteRfqTemplate = async (templateId: string): Promise<void> => {
    return await dapi.delete(`/v1/rfq-templates/${templateId}`)
}

export const updateRfqTemplate = async (
    templateId: string,
    name: string,
    template: RfqTemplateData,
): Promise<TypedDapiRfqTemplate> => {
    return (await dapiNoInterceptors.put(`/v1/rfq-templates/${templateId}`, { name, template }))
        .data
}

export const downloadPdf = async (url: string, fileName: string) => {
    try {
        const response = await dapi
            .post(
                `/v1/pdf`,
                {
                    url,
                },
                {
                    responseType: 'arraybuffer',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/pdf',
                    },
                },
            )
            .then((res) => res.data)

        const blob = new Blob([response as unknown as BlobPart], { type: 'application/pdf' })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()

        // Cleanup
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    } catch (error) {
        console.error('Error downloading PDF:', error)
    }
}

export const getBusinessEstimates = async ({
    after,
    limit,
    accountId,
    from,
    through,
    type,
}: IPaginationQueryParams & {
    accountId?: string[]
    type?: EstimateType[]
    from?: Date
    through?: Date
} = {}): Promise<PaginatedBusinessEstimatesWithAccountInfo> => {
    // Manually encode from and through query parameters.
    // Axios already URL-encodes params, but it follows encodeURI semantics,
    // leaving reserved characters like ":" unescaped. Our backend rejects those,
    // so we wrap them with encodeURIComponent to ensure strict encoding.
    const queryString = []
    if (from) {
        queryString.push(`from=${encodeURIComponent(from.toISOString())}`)
    }
    if (through) {
        queryString.push(`through=${encodeURIComponent(through.toISOString())}`)
    }
    return (
        await dapi.get(`v1/estimates/business?${queryString.join('&')}`, {
            params: { after, limit, accountId, type },
        })
    ).data
}

export const getLogisticsEstimates = ({
    after,
    limit,
    accountId,
    from,
    through,
    source,
    isShipment,
}: IPaginationQueryParams & {
    accountId?: string[]
    source?: string[]
    from?: Date
    through?: Date
    isShipment?: boolean
} = {}): AsyncResult<PaginatedLogisticsEstimatesWithAccountInfo, AxiosError> => {
    return wrapAxiosPromise(
        dapi
            .get(`v1/estimates/logistics?${encodeDateTime(from, through)}`, {
                params: {
                    after,
                    limit,
                    accountId,
                    source,
                    ...(isShipment !== undefined && { is_shipment: isShipment }),
                },
            })
            .then((res) => res.data),
    )
}

export const getAllDataSheets = ({
    after,
    limit,
    accountIds,
    from,
    through,
    type,
}: IPaginationQueryParams & {
    accountIds?: string[]
    type?: string[]
    from?: LocalDate
    through?: LocalDate
} = {}): AsyncResult<PaginatedDataSheets, AxiosError> => {
    return wrapAxiosPromise(
        dapi
            .get(`v1/data-sheets`, {
                params: {
                    after,
                    limit,
                    accountIds,
                    type,
                    ...(from && { from: from.toString() }),
                    ...(through && { through: through.toString() }),
                },
            })
            .then((res) => res.data),
    )
}

export type PaginatedDataSheetEmpty = {
    hasMore: boolean
    nextCursor: string | null
    data: null
} & PartialDataSheet

export const getDataSheetById = ({
    after,
    limit,
    id,
    from,
    through,
    isShipment,
}: IPaginationQueryParams & {
    id?: string
    from?: LocalDate
    through?: LocalDate
    isShipment?: boolean
} = {}): AsyncResult<
    | PaginatedDataSheetBusinessEstimates
    | PaginatedDataSheetLogisticsEstimates
    | PaginatedDataSheetEmpty,
    AxiosError
> => {
    return wrapAxiosPromise(
        dapi
            .get(`v1/data-sheets/${id}/estimates`, {
                params: {
                    after,
                    limit,
                    ...(from && { from: from.toString() }),
                    ...(through && { through: through.toString() }),
                    ...(isShipment !== undefined && { is_real_event: isShipment }),
                },
            })
            .then((res) => res.data),
    )
}
export type Report = {
    id: string
    numCalculations: number | null
    numErrors: number | null
    processedAt: string | null
    createdAt: string
    type: 'logistics' | 'spend'
    status: 'progress' | 'done' | 'failed'
    fileSize: string | null
    filters: {
        accountIds: string[] | null
        from: string | null
        through: string | null
        shipmentStatus: 'shipment' | 'pre-shipment' | null
        source:
            | 'ecotransit'
            | 'lune'
            | 'lune_logistics_v2'
            | 'lune_fintech'
            | 'rfq'
            | 'logistics_user_template'
            | null
    }
}

export type PaginatedReports = {
    hasMore: boolean
    nextCursor: string | null
} & {
    data: Array<Report>
}
export const getAllReports = ({
    after,
    limit,
    from,
    through,
}: IPaginationQueryParams & {
    from?: LocalDate
    through?: LocalDate
} = {}): AsyncResult<PaginatedReports, AxiosError> => {
    console.log(after, limit, from, through)
    // TODO: Replace with actual API call once /reports endpoint is available
    // Returning dummy data for UI development
    const dummyData: PaginatedReports = {
        hasMore: false,
        nextCursor: null,
        data: [
            {
                id: 'report-1',
                numCalculations: 150,
                numErrors: 2,
                processedAt: '2024-01-15T10:30:00Z',
                createdAt: '2024-01-15T09:00:00Z',
                type: 'logistics',
                fileSize: '2.3MB',
                status: 'done',
                filters: {
                    from: '2024-01-01',
                    through: '2024-01-31',
                    source: 'lune',
                    accountIds: null,
                    shipmentStatus: 'shipment',
                },
            },
            {
                id: 'report-2',
                numCalculations: 150,
                numErrors: 2,
                processedAt: '2024-01-15T10:30:00Z',
                createdAt: '2024-01-15T09:00:00Z',
                type: 'spend',
                fileSize: '2.3MB',
                status: 'done',
                filters: {
                    from: '2024-01-01',
                    through: '2024-01-31',
                    source: 'lune',
                    accountIds: [
                        '9aKx7b6nNXMk3YvODnpD1mlW5Od2eLZE',
                        'VndoQ0PZjGMzvYO2LNy6kbgN1eOJx9B8',
                    ],
                    shipmentStatus: 'pre-shipment',
                },
            },
            {
                id: 'report-2',
                numCalculations: 75,
                numErrors: 0,
                processedAt: '2024-01-14T14:20:00Z',
                createdAt: '2024-01-14T13:00:00Z',
                type: 'logistics',
                fileSize: null,
                status: 'failed',
                filters: {
                    from: null,
                    through: null,
                    source: null,
                    accountIds: null,
                    shipmentStatus: 'pre-shipment',
                },
            },
            {
                id: 'report-3',
                numCalculations: null,
                numErrors: null,
                processedAt: null,
                createdAt: '2024-01-16T08:00:00Z',
                type: 'spend',
                fileSize: null,
                status: 'progress',
                filters: {
                    from: '2024-02-01',
                    through: '2024-02-29',
                    source: 'rfq',
                    accountIds: ['lune-1', 'lune-2'],
                    shipmentStatus: 'pre-shipment',
                },
            },
        ],
    }
    return wrapAxiosPromise(Promise.resolve(dummyData))
}

export const batchUpdateLogisticsAnnotations = ({
    ids,
    isShipment,
}: {
    ids: string[]
    isShipment: boolean
}): AsyncResult<BatchUpdateLogisticsAnnotationsResponse, AxiosError> => {
    const request: BatchUpdateLogisticsAnnotationsRequest = {
        estimateIds: ids,
        data: {
            isShipment,
        },
    }
    return wrapAxiosPromise(
        dapi.patch('v1/estimates/logistics/annotations', request).then((res) => res.data),
    )
}

export const batchUpdateSheetEstimatesAnnotations = ({
    sheetId,
    isShipment,
}: {
    sheetId: string
    isShipment: boolean
}): AsyncResult<BatchUpdateLogisticsAnnotationsResponse, AxiosError> => {
    const request: DataSheetAnnotationsUpdateRequest = {
        data: {
            isRealEvent: isShipment,
        },
    }

    return wrapAxiosPromise(
        dapi
            .patch(`v1/data-sheets/${sheetId}/estimates/annotations`, request)
            .then((res) => res.data),
    )
}

export const downloadBusinessEstimateCsv = ({
    accountIds,
    from,
    through,
    estimateTypes,
}: {
    accountIds?: string[]
    from?: Date
    through?: Date
    estimateTypes?: Extract<
        EstimateType,
        'flight' | 'transaction' | 'transaction_document' | 'passenger_transportation' | 'activity'
    >[]
} = {}): AsyncResult<number, AxiosError> => {
    /* This only triggers an asynchronous process. The user will have to download the file from S3 once the estimate
     file is ready */
    return wrapAxiosPromise(
        dapi
            .get(`v1/estimates/business/download?${encodeDateTime(from, through)}`, {
                params: {
                    ...(accountIds && { account_id: accountIds }),
                    ...(estimateTypes && { type: estimateTypes }),
                },
            })
            .then((res) => res.status),
    )
}

export const downloadLogisticsEstimateCsv = ({
    accountIds,
    from,
    through,
    isShipment,
    source,
}: {
    accountIds?: string[]
    from?: Date
    through?: Date
    isShipment?: boolean
    source?: EstimateSource[]
} = {}): AsyncResult<number, AxiosError> => {
    /* This only triggers an asynchronous process. The user will have to download the file from S3 once the estimate
     file is ready */
    return wrapAxiosPromise(
        dapi
            .get(`v1/estimates/logistics/download?${encodeDateTime(from, through)}`, {
                params: {
                    ...(isShipment !== undefined && { is_real_event: isShipment }),
                    ...(source && { source }),
                    ...(accountIds && { account_id: accountIds }),
                },
            })
            .then((res) => res.status),
    )
}

// Returns last 20 active client accounts
export const getRecentClientAccounts = (): AsyncResult<ClientAccountInformation, AxiosError> => {
    return wrapAxiosPromise(dapi.get(`v1/accounts/client`).then((res) => res.data))
}

export const getThirtyDayStatistics = (
    organisationId: string,
): AsyncResult<ThirtyDayStatistics, AxiosError> => {
    return wrapAxiosPromise(
        dapi.get(`v1/organisations/${organisationId}/top-stats`).then((res) => res.data),
    )
}

export const getClientAccountById = (id: string): AsyncResult<ClientAccount, AxiosError> => {
    return wrapAxiosPromise(dapi.get(`v1/accounts/client/${id}`).then((res) => res.data))
}

export function getBusinessEstimatesAnalytics({
    from,
    through,
    accountIds,
}: {
    from: LocalDate
    through: LocalDate
    accountIds?: string[]
}): AsyncResult<GetBusinessEstimateAnalyticsResponse, unknown> {
    return wrapAxiosPromise(
        dapi
            .get(`v1/analytics/business-estimates`, {
                params: {
                    from: from.toString(),
                    through: through.toString(),
                    account_ids: accountIds,
                },
            })
            .then((res) => res.data),
    )
}
